"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { LawFirmsAPI } from "../api/lawFirms";
import type { LawyerDTO } from "../api/types";
import { queryClient } from "../lib/query/queryClient";
import { lawyerSelectors } from "../redux/lawyer/selectors";

const getLawyerLabel = (lawyer: LawyerDTO) =>
  [lawyer.firstName, lawyer.middleName, lawyer.lastName]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();

export type PracticeTeamMemberOption = {
  uid: string;
  label: string;
  email: string;
};

export const firmLawyersKey = (lawFirmId: string) =>
  ["firm-lawyers", lawFirmId] as const;

const EMPTY_TEAM: LawyerDTO[] = [];

/**
 * The signed-in lawyer's firm members, from `GET /api/law_firms/<id>/lawyers`.
 * Firestore rules deny the browser direct reads of `lawyers`, so this goes
 * through the backend. `_lawyerPractice` is unused and kept for callers.
 */
export const usePracticeTeamMembers = (_lawyerPractice?: string | null) => {
  const { law_firm_id } = useSelector(lawyerSelectors.selectAll);
  const lawFirmId = (law_firm_id ?? "").trim();

  const query = useQuery(
    {
      queryKey: firmLawyersKey(lawFirmId),
      queryFn: () => LawFirmsAPI.listLawyers(lawFirmId),
      enabled: Boolean(lawFirmId),
      staleTime: 60_000
    },
    queryClient
  );

  const teamMembers = useMemo<LawyerDTO[]>(() => {
    if (!Array.isArray(query.data)) return EMPTY_TEAM;
    return query.data
      .filter((lawyer) => !lawyer.disabled)
      .sort((a, b) =>
        getLawyerLabel(a).localeCompare(getLawyerLabel(b), undefined, {
          sensitivity: "base"
        })
      );
  }, [query.data]);

  const options = useMemo<PracticeTeamMemberOption[]>(() => {
    return teamMembers.map((m) => ({
      uid: (m.uid ?? "").trim(),
      label: getLawyerLabel(m) || (m.email ?? "").trim() || "Unknown",
      email: (m.email ?? "").trim()
    }));
  }, [teamMembers]);

  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : "Couldn't load team members."
    : null;

  return {
    teamMembers,
    options,
    isLoading: query.isPending && query.fetchStatus !== "idle",
    error,
    refetch: query.refetch
  };
};
